import { memo } from 'react';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';

import * as K from 'common/constants';
import { areEqual, checkIsActive, SearchLogLineProps } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import LogLine from 'components/search/LogLine';
import ELEMENT, { createTestId } from 'common/element';

/**
 * This component represents a single log line when the user has chosen to view search results as log lines rather than
 * in a table. The props are passed down by the react-window library's VariableSizeList component so that this component
 * can be memoized.
 *
 * @prop data                 memoized data passed down from the itemData prop
 * @prop index                index of this line in the data
 * @prop style                styling needed to position the line
 */
function SearchLogLine(props: SearchLogLineProps): JSX.Element {
  const {
    data: {
      filter,
      logEntryEdges,
      settings,
      timezone,
    },
    index,
    style,
  } = props;

  const continuationToken = logEntryEdges[index]?.continuationToken || '';
  const logEntry = logEntryEdges[index]?.logEntry;
  const isActive = checkIsActive(props);

  const key = continuationToken || String(index);
  const logLineClass = classNames(
    'LogLine',
    {
      'LogLine--Active': isActive,
      'LogLine--RegularFont': settings.fontFamily === K.FONT_FAMILY.REGULAR,
    },
  );

  // let line width be determined by the widest of content or container (for striping)
  const styleClone = cloneDeep(style);
  styleClone.minWidth = style.width;
  delete styleClone.width;

  return (
    <div
      style={styleClone}
      className={logLineClass}
      key={key}
      data-continuation-token={continuationToken}
      data-test-id={createTestId(ELEMENT.searchLogsLogLine, index)}
    >
      <LogLine
        index={index}
        continuationToken={continuationToken}
        logEntry={logEntry}
        filter={filter}
        settings={settings}
        timezone={timezone}
      />
    </div>
  );
}

export default memo(SearchLogLine, areEqual);
